import styled from 'styled-components';
import Link from 'next/link';

import { config } from '_config';
import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';
import { ICONS_ALIASES, ICONS_PALETTE } from '_constants/icons';
import { Trademarking_FAQ } from '_constants/faq';

import { Divider } from '_components/atomic/layouts';
import Circle from '_components/atomic/Circle';
import Dots from '_components/atomic/Dots';
import HowSection from '_components/composed/sections/HowSection';
import FancyList from '_components/atomic/FancyList';
import Button from '_components/atomic/Button';
import RegisteringTMInfoSection from '_components/composed/sections/RegisteringTMInfoSection';
import TMPackageInfoSection from '_components/composed/sections/TMPackageInfoSection';
import ProcessSection from '_components/composed/sections/ProcessSection';
import FAQSection from '_components/composed/sections/FAQSection';
import BottomNavigationSection from '_components/composed/sections/BottomNavigationSection';
import ProductBannerSection from '_components/composed/sections/ProductBannerSection';

const { colors, spaces, devices, fontSizes } = theme;

const StyledTrademarkingPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const StyledRegisterTodayContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: min-content;
  background-color: ${colors.green[500]};
  padding: ${spaces.medium}px;
  gap: 20px;
  border-radius: 20px;
  @media ${devices.tablet} {
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: ${spaces.regular}px;
  }
`;

const StyledRegisterTodayCard = styled.div`
  display: flex;
  gap: 16px;
  width: fit-content;
  padding: ${spaces.regular}px;
  background-color: ${colors.green[400]};
  border-radius: 16px;

  @media ${devices.tablet} {
    flex-direction: column;
    justify-content: space-between;
    gap: 0;
    width: 100%;
    margin-bottom: 0;
    padding: ${spaces.regular}px;
  }
`;

const StyledRegisterTodayCardContentBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledRegisterTodayHeader = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.large}px;
  line-height: 150%;
  color: ${colors.white};

  @media ${devices.tablet} {
    font-size: ${fontSizes.regular}px;
    text-align: center;
  }
`;

const StyledText = styled.div`
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.medium}px;
  line-height: 150%;
  white-space: nowrap;
  color: ${colors.green[25]};
  display: flex;
  align-items: center;
  justify-content: center;
  span {
    font-size: ${fontSizes.small}px;
    font-weight: 300;
  }
`;

const TrademarkingPage = () => {
  return (
    <StyledTrademarkingPage>
      <ProductBannerSection
        title="Trademarking"
        text="Secure your business's unique identity and set yourself apart from the competition by trademarking your property. Protect your brand name, logo, or slogan and claim exclusive rights to them."
        buttonsBlock={
          <StyledRegisterTodayContainer>
            <StyledRegisterTodayHeader>
              Our attorney-led trademark package is a fraction of the cost of a
              traditional attorney.
            </StyledRegisterTodayHeader>
            <StyledRegisterTodayCard>
              <StyledRegisterTodayCardContentBlock>
                <Link href={config.trademarkUrl}>
                  <Button
                    color={colors.green[25]}
                    textColor={colors.green[500]}
                    hoverColor={colors.green[25]}
                    hoverTextColor={colors.green[500]}
                    large
                  >
                    Register today
                  </Button>
                </Link>
              </StyledRegisterTodayCardContentBlock>
              <StyledRegisterTodayCardContentBlock>
                <StyledText>
                  from $99<span>&nbsp;+ USPTO fee</span>
                </StyledText>
              </StyledRegisterTodayCardContentBlock>
            </StyledRegisterTodayCard>
          </StyledRegisterTodayContainer>
        }
        backgroundImage={`${config.basePath}/images/trademarking-banner-background.svg`}
        desktopImage={`${config.basePath}/images/trademarking-banner-desktop.png`}
        mobileImage={`${config.basePath}/images/trademarking-banner-mobile.png`}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <HowSection
        title={<>Benefits of Trademarking</>}
        rightPart={
          <FancyList
            items={[
              {
                title: 'Legal protection',
                description:
                  'Legal protection against infringement and unauthorized use of your trademarked property.',
                icon: ICONS_ALIASES.privacy,
                iconPalette: ICONS_PALETTE.indigo[ICONS_ALIASES.privacy],
                color: colors.indigo[25],
                textColor: colors.indigo[500],
              },
              {
                title: 'Exclusive rights',
                description:
                  'Exclusive rights to use your trademarked property, including brand name, logo, and slogan, within the scope of the registration.',
                icon: ICONS_ALIASES.copyright,
                iconPalette: ICONS_PALETTE.green[ICONS_ALIASES.copyright],
                color: colors.green[50],
                textColor: colors.green[500],
              },
              {
                title: 'Increased credibility',
                description:
                  'Increased brand recognition and credibility in the market.',
                icon: ICONS_ALIASES.productIncrement,
                iconPalette: ICONS_PALETTE.blue[ICONS_ALIASES.productIncrement],
                color: colors.blue[25],
                textColor: colors.blue[500],
              },
              {
                title: 'Valuable asset',
                description:
                  'A valuable asset that can be licensed, sold, or used as collateral for loans.',
                icon: ICONS_ALIASES.quality,
                iconPalette: ICONS_PALETTE.orange[ICONS_ALIASES.quality],
                color: colors.orange[50],
                textColor: colors.orange[500],
              },
            ]}
          />
        }
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <RegisteringTMInfoSection />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <TMPackageInfoSection
        items={[
          {
            key: 'Attorney-Led Trademark Filing',
            title: 'Attorney-Led Trademark Filing',
            iconAlias: ICONS_ALIASES.shieldTick,
            iconColor: colors.gray[700],
          },
          {
            key: 'Comprehensive Trademark Search',
            title: 'Comprehensive Trademark Search',
            iconAlias: ICONS_ALIASES.searchZoomIn,
            iconColor: colors.gray[700],
          },
          {
            key: 'Office Action Protection',
            title: 'Office Action Protection',
            iconAlias: ICONS_ALIASES.shieldWithBand,
            iconColor: colors.gray[700],
          },
          {
            key: 'Trademark Monitoring',
            title: 'Trademark Monitoring',
            iconAlias: ICONS_ALIASES.devices,
            iconColor: colors.gray[700],
          },
        ]}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <ProcessSection
        title="The Process"
        activeStepIdx={0}
        steps={[
          {
            title: 'STEP 01',
            subtitle: 'Tell Us About Your Mark',
            description: <span>Fill out our simple questionnaire</span>,
            imageSrc: `${config.basePath}/images/conversation.svg`,
            reverse: false,
          },
          {
            title: 'STEP 02',
            subtitle: 'We’ll do the Research',
            description: (
              <span>
                Our professionals will conduct a comprehensive search, then
                report back to you before filing.
              </span>
            ),
            imageSrc: `${config.basePath}/images/research-paper.svg`,
            reverse: true,
          },
          {
            title: 'STEP 03',
            subtitle: 'Get Your Trademark Filed',
            description: (
              <span>
                Once you approve the trademark search, we’ll get your trademark
                filed within 24-48 hours.
              </span>
            ),
            imageSrc: `${config.basePath}/images/consent.svg`,
            reverse: false,
          },
        ]}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <FAQSection items={Trademarking_FAQ} />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <BottomNavigationSection
        previousService={{
          title: 'Registered Agent',
          link: ROUTES.registeredAgent,
        }}
        nextService={{
          title: 'LLC Incorporation',
          link: ROUTES.llcIncorporation,
        }}
      />

      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />

      <Dots
        color={theme.colors.indigo[500]}
        left={290}
        top={`calc(16% + 10px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        rotate={-90}
        left={49}
        top={`calc(12% + 20px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        right={260}
        top={`calc(19% + 20px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        right={-172}
        top={`calc(16% - 20px)`}
      />

      <Dots color={theme.colors.indigo[500]} left={4} top={`calc(43% - 4px)`} />

      <Dots
        color={theme.colors.indigo[500]}
        right={60}
        top={`calc(49% - 6px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        left={290}
        top={`calc(84% + 20px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        rotate={90}
        left={-134}
        top={`calc(81% + 10px)`}
      />

      <Dots
        color={theme.colors.indigo[500]}
        right={266}
        top={`calc(77% + 30px)`}
      />
      <Circle
        gradient={{
          from: theme.colors.indigo[300],
          to: theme.colors.indigo[50],
        }}
        size={686}
        right={-172}
        top={`calc(74% - 20px)`}
      />
    </StyledTrademarkingPage>
  );
};

export default TrademarkingPage;
